import React, { useState, useEffect } from 'react';
import Modal from '../modal/modal.jsx';
import styles from './DashboardModal.css';
import apiClient from '../../utils/apiClient';
import activityTracker from '../../utils/activityTracker';

const DashboardModal = ({ isOpen, onClose }) => {
    const [userData, setUserData] = useState({
        activityStats: {
            monthlyActivity: {
                activeDays: 0,
                totalScreenTime: 0,
                projectsCreated: 0
            },
            dailyActivity: [], // Initialize as empty array
            projects: {
                total: 0,
                recent: []
            }
        }
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const userId = localStorage.getItem('userId');
                if (!userId) return;

                const response = await apiClient.get(`/api/users/${userId}/dashboard`);
                setUserData(response.data);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            fetchDashboardData();
        }
    }, [isOpen]);

    // Remove the old activity tracking effect since it's now handled by activityTracker

    const renderActivityChart = () => {
        const currentHour = new Date().getHours();
        const maxDuration = Math.max(
            ...userData.activityStats.dailyActivity.map(hour => hour.duration),
            1 // Fallback to 1 to avoid division by zero
        );
        
        return (
            <div className={styles.activityChart}>
                <h3>Today's Activity</h3>
                <div className={styles.hourlyBars}>
                    {userData.activityStats.dailyActivity.map((hour) => (
                        <div 
                            key={hour.hour}
                            className={styles.hourBar}
                            style={{ 
                                height: `${(hour.duration / maxDuration) * 100}%`,
                                opacity: hour.duration > 0 ? 1 : 0.1,
                                backgroundColor: hour.hour === currentHour ? '#64B5F6' : '#4CAF50'
                            }}
                            title={`${hour.hour.toString().padStart(2, '0')}:00 - ${hour.duration} minutes`}
                        />
                    ))}
                </div>
                <div className={styles.timeLabels}>
                    {[0, 6, 12, 18, 23].map(hour => (
                        <span key={hour}>{`${hour}:00`}</span>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <Modal
            className={styles.modalContent}
            contentLabel="Dashboard"
            onRequestClose={onClose}
            isOpen={isOpen}
        >
            {loading ? (
                <div className={styles.loading}>Loading activity data...</div>
            ) : (
                <div className={styles.dashboardContent}>
                    {/* Monthly Overview */}
                    <div className={styles.statsGrid}>
                        <div className={styles.statCard}>
                            <h3>Active Days</h3>
                            <div className={styles.statValue}>
                                {userData.activityStats.monthlyActivity.activeDays}
                                <span className={styles.statLabel}>this month</span>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <h3>Screen Time</h3>
                            <div className={styles.statValue}>
                                {(() => {
                                    const totalMinutes = userData.activityStats.monthlyActivity.totalScreenTime;
                                    const hours = Math.floor(totalMinutes / 60);
                                    const minutes = totalMinutes % 60;
                                    return (
                                        <>
                                            {hours}h {minutes}m
                                            <span className={styles.statLabel}>this month</span>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <h3>Projects Created</h3>
                            <div className={styles.statValue}>
                                {userData.activityStats.monthlyActivity.projectsCreated}
                                <span className={styles.statLabel}>this month</span>
                            </div>
                        </div>
                    </div>

                    {/* Daily Activity Chart */}
                    {renderActivityChart()}

                    {/* Recent Projects */}
                    <div className={styles.recentProjects}>
                        <h3>Recent Projects</h3>
                        <div className={styles.projectsList}>
                            {userData?.activityStats?.projects?.recent?.map((project, index) => (
                                <div key={project._id || index} className={styles.projectItem}>
                                    <span className={styles.projectTitle}>{project.title}</span>
                                    <span className={styles.projectDate}>
                                        {new Date(project.createdAt).toLocaleDateString()}
                                    </span>
                                </div>
                            )) || <div>No recent projects</div>}
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default DashboardModal;